'use client'

import Link from 'next/link'
import { Menu, Sparkles, Layers, CreditCard, LogIn } from 'lucide-react'
import { Logo } from '@/components/logo'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { ThemeToggle } from '@/components/theme-toggle'
import { LanguageToggle } from '@/components/language-toggle'
import { UserMenu } from '@/components/auth/UserMenu'
import { AppGrid } from '@/components/nav/app-grid'
import { AppGridMenu } from '@/components/nav/app-grid-menu'
import { NavSearch } from '@/components/nav/nav-search'
import { GuideButton } from '@/components/guide/guide-button'
import { cn } from '@/lib/utils'
import { DASHBOARD_MAX_WIDTH } from '@/lib/layout'

export function Header() {
	const t = useTranslations('nav')
	const [scrolled, setScrolled] = useState(false)
	const [mobileOpen, setMobileOpen] = useState(false)
	const { isAuthenticated, isLoading } = useAuth()
	const pathname = usePathname()
	const isDashboard = pathname?.startsWith('/dashboard')

	useEffect(() => {
		const handleScroll = () => {
			const simpleBarContent = document.querySelector('.simplebar-content-wrapper')
			const scrollTop = simpleBarContent?.scrollTop || window.scrollY
			setScrolled(scrollTop > 20)
		}

		window.addEventListener('scroll', handleScroll, { passive: true })
		const simpleBarContent = document.querySelector('.simplebar-content-wrapper')
		if (simpleBarContent) {
			simpleBarContent.addEventListener('scroll', handleScroll, { passive: true })
		}
		handleScroll()

		return () => {
			window.removeEventListener('scroll', handleScroll)
			if (simpleBarContent) {
				simpleBarContent.removeEventListener('scroll', handleScroll)
			}
		}
	}, [])

	return (
		<header className="fixed top-0 left-0 right-0 z-50 md:px-4 md:pt-3 lg:pt-4">
			<div
				className={cn(
					'mx-auto flex h-14 items-center justify-between px-4 md:px-6 md:rounded-xl border transition-all duration-300',
					isDashboard ? DASHBOARD_MAX_WIDTH : 'max-w-6xl',
					scrolled
						? 'bg-background/80 backdrop-blur-md border-border/50 shadow-lg'
						: 'bg-transparent border-transparent'
				)}
			>
				{/* Signed in, the brand mark leads back into the app - "/" would only bounce off the proxy's
				    signed-in redirect anyway. */}
				<Link href={isAuthenticated ? '/dashboard/chat' : '/'} className="flex items-center gap-2">
					<div className="h-7 w-7 rounded-lg bg-blue-600 flex items-center justify-center">
						<Logo className="h-4 w-auto text-white" />
					</div>
					<span className="text-lg font-bold">Evuno</span>
				</Link>

				{!isDashboard && (
					<nav className="hidden md:flex items-center gap-6">
						<Link href="/#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
							{t('features')}
						</Link>
						<Link
							href="/#capabilities"
							className="text-sm text-muted-foreground hover:text-foreground transition-colors"
						>
							{t('capabilities')}
						</Link>
						<Link href="/#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
							{t('pricing')}
						</Link>
					</nav>
				)}

				<div className="hidden md:flex items-center gap-1">
					{/* Dashboard only: search covers the user's own chats, projects and documents, which do not
					    exist as a concept on the marketing pages. */}
					{isAuthenticated && isDashboard && <NavSearch />}
					{isAuthenticated && isDashboard && <GuideButton />}
					<LanguageToggle />
					<ThemeToggle />
					{isAuthenticated && <AppGridMenu />}
					<UserMenu />
				</div>

				<div className="flex md:hidden items-center">
					<Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
						<SheetTrigger asChild>
							<Button variant="ghost" size="icon">
								<Menu className="h-5 w-5" />
							</Button>
						</SheetTrigger>
						<SheetContent side="right" className="w-[280px] flex flex-col p-0">
							<SheetHeader className="sr-only">
								<SheetTitle>{t('menu')}</SheetTitle>
							</SheetHeader>

							<div className="flex items-center gap-2 px-6 py-5 border-b">
								<div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center">
									<Logo className="h-5 w-auto text-white" />
								</div>
								<span className="text-lg font-bold">Evuno</span>
							</div>

							<nav className="flex-1 px-3 py-4">
								{isAuthenticated && (
									<div className="space-y-2">
										<span className="block px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
											{t('apps.title')}
										</span>
										<AppGrid onNavigate={() => setMobileOpen(false)} />
									</div>
								)}

								{!isDashboard && (
									<div className={cn('space-y-1', isAuthenticated && 'mt-6 pt-6 border-t')}>
										<Link
											href="/#features"
											onClick={() => setMobileOpen(false)}
											className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent rounded-lg transition-colors"
										>
											<Sparkles className="h-4 w-4 text-muted-foreground" />
											{t('features')}
										</Link>
										<Link
											href="/#capabilities"
											onClick={() => setMobileOpen(false)}
											className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent rounded-lg transition-colors"
										>
											<Layers className="h-4 w-4 text-muted-foreground" />
											{t('capabilities')}
										</Link>
										<Link
											href="/#pricing"
											onClick={() => setMobileOpen(false)}
											className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent rounded-lg transition-colors"
										>
											<CreditCard className="h-4 w-4 text-muted-foreground" />
											{t('pricing')}
										</Link>
									</div>
								)}

								<div className="mt-6 pt-6 border-t">
									{isLoading ? (
										<div className="flex items-center gap-3 px-3 py-2.5">
											<div className="h-4 w-4 rounded-full bg-muted animate-pulse" />
											<div className="h-4 w-20 rounded bg-muted animate-pulse" />
										</div>
									) : isAuthenticated ? (
										<div className="px-1">
											<UserMenu />
										</div>
									) : (
										<Link
											href="/auth/signin"
											onClick={() => setMobileOpen(false)}
											className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent rounded-lg transition-colors"
										>
											<LogIn className="h-4 w-4 text-muted-foreground" />
											{t('signIn')}
										</Link>
									)}
								</div>
							</nav>

							<div className="px-6 py-4 border-t bg-muted/30">
								<div className="flex items-center justify-between">
									<span className="text-xs text-muted-foreground font-medium">{t('preferences')}</span>
									<div className="flex items-center gap-2">
										<LanguageToggle />
										<ThemeToggle />
									</div>
								</div>
							</div>
						</SheetContent>
					</Sheet>
				</div>
			</div>
		</header>
	)
}
